/* eslint-disable import/no-cycle */
import { Page, StatusType } from '../api.types';
import { UserRole } from '../authentication/authentication.types';
import {
  BrokerRateModel,
  CarModelModel,
  DeskGroupModel,
  DeskModel,
  ItemGroupType,
  ProductModel
} from '../core/core.types';


/*
* Discount rules
*/

export interface DiscountRuleModel {
  id?: number,
  discountPercentage: number;
  pickupFrom: string;
  pickupTo: string;
  requestFrom: string;
  requestTo: string;
  daysFrom: number;
  daysTo: number;
  statusType: StatusType;
  brokerRates: DiscountRuleBrokerRateModel[];
  brokers: DiscountRuleBrokerModel[];
  products: DiscountRuleProductModel[];
  desks: DiscountRuleDeskModel[];
  carClasses: DiscountRuleCarClassModel[];

  modifiedAt?: string;
  modifiedBy?: string;
  createdBy?: string;
  createdAt?: string;

}

export interface DiscountRuleCarClassModel {
  acrissCode: string;
}

export interface DiscountRuleBrokerRateModel {
  code: string;
}

export interface DiscountRuleBrokerModel {
  id: number;
}

export interface DiscountRuleProductModel {
  code: string;
}

export interface DiscountRuleDeskModel {
  code: string;
}

export const discountRuleViewRoles = [ UserRole.RATE_MANAGE ];
export const discountRuleEditRoles = [ UserRole.RATE_MANAGE ];

export interface DiscountRuleFilters {
  pickupDate?: string;
  desks?: string;
  brokers?: string;
  pageNumber?: number;
}

/*
* Promo Codes
*/
export interface PromoCodeModel {
  id?: number;
  code: string;
  comment: string;
  discountPercentage: number;
  pickupFrom: string;
  pickupTo: string;
  requestFrom: string;
  requestTo: string;
  daysFrom: number;
  daysTo: number;
  statusType: StatusType;
  brokerRates: PromoCodeBrokerRateModel[];
  carClasses: PromoCodeCarClassModel[];
  desks: PromoCodeDeskModel[];
  blackoutPeriods: PromoCodeBlackoutPeriod[];
  brokers: PromoCodeBrokerModel[];

  modifiedAt?: string;
  modifiedBy?: string;

  createdAt?: string;
  createdBy?: string;
}

export interface PromoCodeBlackoutPeriod {
  validFrom: string;
  validTo: string;
}

export interface PromoCodeBrokerRateModel {
  brokerRateCode: string;
}

export interface PromoCodeBrokerModel {
  id: number;
}

export interface PromoCodeCarClassModel {
  acrissCode: string;
}

export interface PromoCodeDeskModel {
  deskCode: string;
}

export const promoCodeViewRoles = [ UserRole.SUPPLIER_WEBSITE_MANAGE ];
export const promoCodeEditRoles = [ UserRole.SUPPLIER_WEBSITE_MANAGE ];

export interface PromoCodeFilters {
  combinedFilterField?: string;
  pickupDate?: string;
  requestDate?: string;
  desks?: string;
  carClasses?: string;
  status?: StatusType;
  pageNumber?: number;
}

/*
* Item Prices
*/

export interface ItemPriceModel {
  id: number;
  price: number;
  isTaxable: boolean;
  isTaxIncluded: boolean;
  maxPrice: number;
  unitType: UnitType;
  statusType: StatusType;
  coreItemId: number;
  excess: ExcessModel;
  itemOtaCode: string;
  itemGroupType: ItemGroupType;
  supplierReferenceCode: string;
  carClasses: ItemPriceCarClassModel[];
  validFrom: string;

  modifiedAt: string;
  modifiedBy: string;

  createdBy: string;
  createdAt: string;
}

export interface ItemPriceModelBrokerRate extends ItemPriceModel {
  selectedCarClasses: string[];
  originalId: number;
  itemOtaCodeCode: string;
  collectedByBroker: boolean;
}

export interface ItemPriceRequest {
  price: number;
  isTaxable: boolean;
  isTaxIncluded: boolean;
  maxPrice: number;
  unitType: UnitType;
  statusType: StatusType;
  coreItemId: number;
  excess?: ExcessModel;
  itemOtaCode: string;
  itemGroupType: ItemGroupType;
  supplierReferenceCode?: string;
  carClasses?: ItemPriceCarClassModel[];
  validFrom: string;
}

export interface ExcessModel {
  excessCurrencyCode: string;
  excessAmount: number;
  depositAmount: number;
}

export interface MandatoryItemPriceModel {
  itemPriceId: number;
  itemPriceException?: ItemPriceExceptionModel;
  collectedByBroker: boolean;
}

export enum UnitType {
  PER_DAY = 'PER_DAY',
  FIXED = 'FIXED',
  PER_CENT = 'PER_CENT'
}

export const UnitTypeOptions = [
  { value: UnitType.PER_DAY, label: 'PER DAY' },
  { value: UnitType.FIXED, label: 'FIXED' },
  { value: UnitType.PER_CENT, label: 'PERCENT' }
];

export const itemPriceViewRoles = [ UserRole.RATE_MANAGE, UserRole.ITEM_PRICE_VIEW ];
export const itemPriceEditRoles = [ UserRole.RATE_MANAGE ];

export interface ItemPriceFilters {
  itemGroupType?: string;
  statusType?: string;
}

/*
* Stop Sell
*/

export interface StopSellModel {
  id?: number;
  statusType: StatusType;
  validTo: string;
  validFrom: string;
  timeToPickup: number;
  considerOnlyDeskOpeningHours: boolean;
  rentalDays: number;
  comment: string;
  ruleType: StopSellRuleType;
  stopSellBrokers: StopSellBrokerModel[];
  stopSellCarClasses: StopSellCarClassModel[];
  stopSellDesks: StopSellDeskModel[];
  stopSellProducts: StopSellProductModel[];
  createdByApi?: boolean;

  modifiedAt?: string;
  modifiedBy?: string;
  createdBy?: string;
  createdAt?: string;
}

export interface StopSellBrokerModel {
  brokerId: number;
}

export interface StopSellProductModel {
  productId: number;
}

export interface StopSellDeskModel {
  deskCode: string;
}

export interface StopSellCarClassModel {
  acrissCode: string;
}

export interface StopSellFilters {
  comment?: string;
  date?: string;
  desks?: string;
  carClasses?: string;
  brokers?: string;
  pageNumber?: number;
  ruleType?: string;
}

export enum StopSellRuleType {
  PICKUP = 'PICKUP',
  OVERLAP = 'OVERLAP',
  MIN_TIME_TO_PICKUP = 'MIN_TIME_TO_PICKUP',
  BLOCK_SHORT_RENTALS = 'BLOCK_SHORT_RENTALS'
}

export const StopSellRuleTypeOptions = [
  { value: StopSellRuleType.PICKUP, label: 'PICKUP' },
  { value: StopSellRuleType.OVERLAP, label: 'OVERLAP' },
  { value: StopSellRuleType.MIN_TIME_TO_PICKUP, label: 'MINIMUM TIME TO PICKUP' },
  { value: StopSellRuleType.BLOCK_SHORT_RENTALS, label: 'BLOCK SHORT RENTALS' }
];

export const stopSellViewRoles = [ UserRole.RATE_MANAGE, UserRole.STOP_SELL_MANAGE, UserRole.STOP_SELL_VIEW ];
export const stopSellEditRoles = [ UserRole.RATE_MANAGE, UserRole.STOP_SELL_MANAGE ];

/*
* Mandatory Items
*/

export interface ItemPriceExceptionModel {
  price: number;
  maxPrice?: number;
  supplierReferenceCode?: string;
}

export interface MandatoryItemModel {
  itemPriceId: number;
  itemPriceException?: ItemPriceExceptionModel;
  collectedByBroker: boolean;
}

export interface ItemPriceCarClassModel {
  id?: number;
  acrissCodes: string[];
  itemPriceException?: ItemPriceExceptionModel;
}

/*
* Rates
*/

export interface RateModel {
  ratePlanPeriod: RatePlanPeriodModel;
  baseAmount: number;
  model: string;
  rowNumber: number;
  carModelInfo: RatePlanCarModel;
}

export interface RatePlanCarModel {
  carModelId: number;
  acrissCode: string;
  minDriverAge: number;
  youngDriverAge?: number;
}

export interface RatePlanModel {
  id: number;
  supplierCode: string;
  statusType: StatusType;
  rateType: RateType;
  coreProductId: number;
  name: string;
  validFrom: string;
  validTo: string;
  priority: number;
  pickupDeskGroupId: number;
  standardPlanId: number;
  rates: RateModel[];

  modifiedAt: string;
  modifiedBy: string;
}

export interface RatePlanListAllModel {
  id: number;
  code: string;
  statusType: StatusType;
  rateType: RateType;
  coreProductId: number;
  name: string;
  validFrom: string;
  validTo: string;
  priority: number;
  pickupDeskGroupId: number;
  standardPlanId: number;

  modifiedAt: string;
  modifiedBy: string;
}

export interface RatePlanPeriodModel {
  id: number;
  daysFrom: number;
  pricePeriodType: PricePeriodType;
  statusType: StatusType;
}

export interface PickupDeskGroupModel {
  groupId: number;
  groupName: string;
}

export interface RatePlanRequest {
  statusType: StatusType;
  rateType: RateType;
  coreProductId: number;
  name: string;
  validFrom: string;
  validTo: string;
  pickupDeskGroupId: number;
  rates: RateRequest[];
  standardPlanId?: number;
}

export interface RateRequest {
  ratePlanPeriodId: number;
  baseAmount: number;
  rowNumber: number;
  carModelInfo: RatePlanCarModel;
}

export interface RatePlanPatchRequest {
  statusType?: StatusType;
  validFrom?: string;
  validTo?: string;
}

export interface AffectedRatePlanModel {
  id: number;
  name: string;
  validFrom: string;
  validTo: string;
  actionResultType: RatePlanActionResultType;
}

export enum RatePlanActionResultType {
  ALTERED = 'ALTERED',
  DELETED = 'DELETED'
}

export enum RateType {
  BASE_RATE = 'BASE_RATE',
  ADJUSTMENT = 'ADJUSTMENT'
}

export enum PricePeriodType {
  FIXED = 'FIXED',
  PER_DAY = 'PER_DAY',
  PER_CENT = 'PER_CENT'
}

export const ratePlanRoles = [ UserRole.RATE_MANAGE ];

/*
* Rates Overivew
*/

export interface RatesOverviewElementModel {
  groupedByValue: number;
  startDate: string;
  endDate: string;
  ratePeriods: RatePeriod[];
}

export interface RatePeriod {
  rentalDays: number;
  carModelId: number;
  rate: OverviewRateModel;
}

export interface ListAllRatesParameters {
  startDate: string;
  endDate: string;
  brokerRateCode: string;
  deskCode: string;
  groupBy?: string;
}

export interface RatesGroupedByRentalDays {
  rentalDays: number;
  carModelAcrissCode?: string;
  carModelName?: string;
  stopSells: StopSellPeriod[];
  datedRatePeriods: DatedRatePeriod[];
}

export interface DatedRatePeriod {
  startDate: string;
  endDate: string;
  totalPerDay: number;
  mandatoryItems: DatedRatePeriodMandatoryItemModel[];
  mandatoryItemPriceTotal: number;
  baseAmount: number;
  baseRateAdjustmentAmount: number;
  discountAmount: number;
  safAmount: number;
  vatAmount: number;
  discountPercentage: number;
  baseRateAdjustmentPercentage: number;
  adjustedVehicleBaseRateAmount: number;
  vatPercentage: number;
  safPercentage: number;
  isAdjustment?: boolean;
}

export interface DatedRatePeriodMandatoryItemModel {
  name: string,
  price: number;
}

export interface OverviewRateModel {
  totalPerDay: number;
  mandatoryItems: RateOverviewMandatoryItemModel[];
  mandatoryItemPriceTotal: number;
  ratePlanId: number;
  isAdjustment: boolean;
  baseRateAmount: number;
  baseRateAdjustmentAmount: number;
  discountAmount: number;
  safAmount: number;
  vatAmount: number;
  discountPercentage: number;
  baseRateAdjustmentPercentage: number;
  adjustedVehicleBaseRateAmount: number;
  vatPercentage: number;
  safPercentage: number;
}

export interface RateOverviewMandatoryItemModel {
  id: number;
  price: number;
}

export interface GetOneRateParameters {
  rateId: number;
  brokerRateCode: string;
  deskCode: string;
}

export interface StopSellsOverviewElementModel {
  acrissCode: string;
  stopSells: StopSellPeriod[];
}

export interface StopSellPeriod {
  startDateTime: string;
  endDateTime: string;
  originalStartDateTime: string;
  originalEndDateTime: string;
  ruleType: StopSellRuleType;
  stopSellId: number;
  rentalDays: number;
}

export interface RateStopSellParameters {
  startDate: string;
  endDate: string;
  brokerRateCode: string;
  deskCode: string;
}

export interface RatePlanViewComponents {
  carModels: Page<CarModelModel>;
  deskGroups: Page<DeskGroupModel>;
  products: Page<ProductModel>;
  ratePlans: Page<RatePlanListAllModel>;
  selectedRatePlan: RatePlanModel;
  ratePlanPeriods: Page<RatePlanPeriodModel>;
}

export interface StopSellViewContent {
  carModels: Page<CarModelModel>;
  desks: Page<DeskModel>;
  products: Page<ProductModel>;
  stopSells: Page<StopSellModel>;
  brokerRates: Page<BrokerRateModel>;
}

export enum GroupBy {
  CAR_MODEL = 'CAR_MODEL',
  RENTAL_DAYS = 'RENTAL_DAYS'
}

export const GroupByOptions = [
  { value: GroupBy.CAR_MODEL, label: 'CAR MODEL' },
  { value: GroupBy.RENTAL_DAYS, label: 'RENTAL DAYS' }
];

/*
* One way fees
*/

export const oneWayFeeEditRoles = [ UserRole.DESK_MANAGE, UserRole.RATE_MANAGE ];

export interface OneWayFeeModel {
  id?: any;
  supplierCode?: string;
  pickupDeskCode: string;
  returnDeskCode: string;
  price?: number | null;
  supplierReferenceCode?: string | null;
}

export interface OneWayFeeRequest {
  pickupDeskCode: string;
  returnDeskCode: string;
  price?: number;
  supplierReferenceCode?: string;
}

export interface OneWayFeePriceItem {
  id: string;
  value?: number;
  initialValue?: number;
  rowItem: OneWayFeeRequest;
}

export interface OneWayFeeSupplierReferenceItem {
  id: string;
  value?: string;
  initialValue?: string;
  rowItem: OneWayFeeRequest;
}

export interface OneWayFeeItemPriceModel {
  id: number;
  isTaxable: boolean;
  isTaxIncluded: boolean;

  createdBy?: string;
  createdAt?: string;
  modifiedBy?: string;
  modifiedAt?: string;
}

export interface OneWayFeeItemPriceRequest {
  isTaxable: boolean;
  isTaxIncluded: boolean;
}

/*
* Cache status
*/

export interface PriceCalculatorCacheStatus {
  supplier: string;
  lastUpdate: string;
  updateStatus: UpdateStatus;
}

export interface CoreCacheStatus {
  unPublishedChangesFound: boolean;
  updateStartedAt: string;
}

export enum UpdateStatus {
  UPDATE_IN_PROGRESS = 'UPDATE_IN_PROGRESS',
  UPDATE_FINISHED = 'UPDATE_FINISHED'
}

/*
* Cache status
*/

export interface OnRequestRuleRequest {
  statusType: StatusType;
  validFrom: string;
  validTo: string;
  onRequestRuleCarClasses: OnRequestRuleCarClassModel[];
  onRequestRuleDesks: OnRequestRuleDeskModel[];
}

export interface OnRequestRuleModel {
  id: number;
  supplierCode: string;
  statusType: StatusType;
  validTo: string;
  validFrom: string;
  onRequestRuleCarClasses: OnRequestRuleCarClassModel[];
  onRequestRuleDesks: OnRequestRuleDeskModel[];

  modifiedAt?: string;
  modifiedBy?: string;
  createdBy?: string;
  createdAt?: string;
}

export interface OnRequestRuleDeskModel {
  deskCode: string;
}

export interface OnRequestRuleCarClassModel {
  acrissCode: string;
}

export interface OnRequestRuleFilters {
  carClass?: string;
  desk?: string;
  pageNumber?: number;
}

export interface RatesSelectedItem {
  value: string;
  label: string;
}

export const onRequestRuleViewRoles = [ UserRole.RATE_MANAGE, UserRole.STOP_SELL_MANAGE, UserRole.STOP_SELL_VIEW ];
export const onRequestRuleEditRoles = [ UserRole.RATE_MANAGE, UserRole.STOP_SELL_MANAGE ];

/*
* Rates request test
*/

export const ratesRequestTestViewRoles = [ UserRole.RATE_MANAGE ];

export interface RatesRequestTestParameters {
  brokerRate: string;
  pickupLocation: string;
  dropoffLocation: string;
  pickupDateTime: string;
  dropoffDateTime: string;
  residency: string;
  age: number;
  promoCode: string;
}
